<template>
  <auth-template>
    <registration-poster slot="poster" />

    <div :class="$style.container">
      <div :class="$style.header">
        <auth-header :title="$t('get.cash.now')" />
      </div>

      <div :class="$style.form">
        <registration-form :loading="loading" @submit="handleSubmit" />
        <p :class="$style.license" v-html="displayPrivacyText"></p>
      </div>

      <div :class="$style.login">
        <p>{{ $t('already.have.account') }}</p>
        <p><router-link to="login" data-test="registrationRouterLink">{{ $t('sign.in') }}</router-link></p>
      </div>
    </div>
  </auth-template>
</template>

<script>
import { mapMutations } from 'vuex';

import { apiCommon } from 'lib/utils/axios';
import { getQueryParamsAsObject } from 'lib/utils/url';

import { DOCS_URL, API_URL } from 'root/.env';

import { checkPartnerKey } from 'services/common';
import enterEmailRequest from 'api/requests/registration/emailEnter';

import HTTPService from '@/services/Http';
import { PARTNER_DATA_KEY } from '@/const';

import RegistrationPoster from 'molecules/auth/RegistrationPoster';
import RegistrationForm from 'organisms/auth/RegistrationForm';
import AuthHeader from 'organisms/auth/AuthHeader';
import AuthTemplate from 'templates/AuthTemplate';

export default {
  name: 'RegistrationView',
  components: {
    RegistrationPoster,
    RegistrationForm,
    AuthHeader,
    AuthTemplate,
  },
  head: {
    title: {
      inner: 'Регистрация',
    },
    meta: [
      { name: 'description', content: 'Регистрация в Capusta.Space. Принимай деньги с любыми целями. Делаешь сайт, продаёшь в Instagram, собираешь инвестиции или фрилансишь — монетизируй образ жизни вместе с Capusta.Space! Легко, молниеносно, безопасно.', id: 'desc' },
    ],
    script: [
      {
        inner: '(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");ym(56244937, "init", {clickmap:true,trackLinks:true,accurateTrackBounce:true,webvisor:true});',
        type: 'text/javascript',
      },
      {
        inner: '!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version="2.0";n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,"script","https://connect.facebook.net/en_US/fbevents.js");fbq("init", "1175137569342493");fbq("track", "PageView");',
        type: 'text/javascript',
      },
      {
        inner: '!function(){var t=document.createElement("script");t.type="text/javascript",t.async=!0,t.src="https://vk.com/js/api/openapi.js?167",t.onload=function(){VK.Retargeting.Init("VK-RTRG-470699-8QMTl"),VK.Retargeting.Hit()},document.head.appendChild(t)}();',
        type: 'text/javascript',
      },
      {
        inner: '(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({"gtm.start": new Date().getTime(),event:"gtm.js"});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!="dataLayer"?"&l="+l:"";j.async=true;j.src="https://www.googletagmanager.com/gtm.js?id="+i+dl;f.parentNode.insertBefore(j,f);})(window,document,"script","dataLayer","GTM-WKBLDC4");',
        type: 'text/javascript',
      },
    ],
    noscript: [
      {
        inner: '<div><img src="https://mc.yandex.ru/watch/56244937" style="position:absolute; left:-9999px;" alt="" /></div>"/>',
      },
      {
        inner: '<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1175137569342493&ev=PageView&noscript=1"/>',
      },
      {
        inner: '<img src="https://vk.com/rtrg?p=VK-RTRG-470699-8QMTl" style="position:fixed; left:-999px;" alt=""/>',
      },
      {
        inner: '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WKBLDC4" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
      },
    ],
  },
  beforeRouteEnter(to, from, next) {
    if (!to.params.code) {
      next();
      return;
    }

    next({
      name: 'registration',
      query: {
        partnerLinkKey: to.params.code,
      },
    });
  },
  async created() {
    this.parseUTMSources();
    this.parsePartnerLink();
    this.checkPartnerKey();
  },
  mounted() {
    // eslint-disable-next-line no-undef
    fbq('track', 'InitiateCheckout');
  },
  data() {
    return {
      loading: false,
      isShownOfferta: false,
    };
  },
  computed: {
    agreementFile() {
      return `${DOCS_URL}/Agreement.pdf`;
    },
    policyPrivacy() {
      return `${DOCS_URL}/pp-privacy.pdf`;
    },
    partnerLinkKey() {
      if (this.$route.query.partnerLinkKey) {
        return this.$route.query.partnerLinkKey;
      }

      const partnerJson = localStorage.getItem(PARTNER_DATA_KEY);

      if (this.$validate.isNull(partnerJson)) {
        return null;
      }

      return JSON.parse(partnerJson).partnerLinkKey || null;
    },
    isPartnerLinkKey() {
      return this.$validate.isNotNull(this.partnerLinkKey);
    },
    displayPrivacyText() {
      if (this.isShownOfferta) {
        return this.$t('registration.privacy.policy.and.offerta', {
          agreementFile: this.policyPrivacy,
          getPartnerOfferLink: `${API_URL}/offer?partnerLinkKey=${this.partnerLinkKey}`,
          dataTestPolicy: 'data-test="registrationPolicyLink"',
          dataTestOfferta: 'data-test="registrationOffertaLink"',
        });
      }

      return this.$t('registration.privacy.policy', {
        agreementFile: this.policyPrivacy,
        dataTestPolicy: 'data-test="registrationPolicyLink"',
      });
    },
  },
  methods: {
    getParams() {
      const rawString = window.location.search.slice(1);
      const getParams = getQueryParamsAsObject(decodeURIComponent(rawString));

      return getParams;
    },
    preparedUrlParams(getParams, parameterList) {
      const filteredParams = Object.keys(getParams).filter(parameter => parameterList.includes(parameter));

      const preparedParams = filteredParams.reduce((acc, curr) => {
        acc[curr] = getParams[curr] !== 'undefined' ? getParams[curr] : '';
        return acc;
      }, {});

      return preparedParams;
    },
    parseUTMSources() {
      const UTM_LIST = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_content',
        'utm_term',
      ];

      const getParams = this.getParams();
      const preparedParams = this.preparedUrlParams(getParams, UTM_LIST);

      localStorage.setItem('utm_sources', JSON.stringify(preparedParams));
    },
    parsePartnerLink() {
      const PARTNER_LINK_PARAMS = [
        'partnerLinkKey',
      ];

      const getParams = this.getParams();
      const preparedParams = this.preparedUrlParams(getParams, PARTNER_LINK_PARAMS);

      if (this.$validate.isEmptyObject(preparedParams)) {
        return;
      }

      localStorage.setItem(PARTNER_DATA_KEY, JSON.stringify(preparedParams));
    },
    async checkPartnerKey() {
      if (!this.isPartnerLinkKey) {
        return;
      }

      try {
        await checkPartnerKey(this.partnerLinkKey);
        this.isShownOfferta = true;
      } catch {
        this.isShownOfferta = false;
      }
    },
    async handleSubmit(data) {
      // eslint-disable-next-line no-undef
      ym(56244937, 'reachGoal', 'registration');

      this.loading = true;

      // TODO: @danealton метод проверки email'а должен быть POST'ом и возвращать корректный ответ
      try {
        await HTTPService.checkEmail(data.email);

        this.$alert({
          type: 'success',
          text: this.$t('restore.access'),
        });

        this.$router.push({
          name: 'restore.password',
          params: { email: data.email },
        });
      } catch (error) {
        console.error(error);

        // TODO: @danealton не правильно закладывать механизм регистрации в блок обработки ошибки при проверке email'а
        this.SET_REGISTRATION_PROCESS({
          state: 'LOGIN',
          data: data.email,
        });

        this.SET_REGISTRATION_PROCESS({
          state: 'PHONE',
          data: data.phone,
        });

        this.SET_REGISTRATION_PROCESS({
          state: 'PROMO_CODE',
          data: data.promoCode,
        });

        enterEmailRequest(apiCommon, {
          email: data.email,
          phone: data.phone,
          promoСode: data.promoСode,
        });

        this.$router.push({ name: 'registration.account' });
      }

      this.loading = false;
    },
    ...mapMutations(['SET_REGISTRATION_PROCESS']),
  },
};
</script>

<style lang="sass" module>
  .container
    position: relative
    margin-bottom: 100px

    +respond-to('medium')
      margin-bottom: 0

    &:after
      content: ''
      width: 860px
      height: 860px
      position: absolute
      left: 50%
      bottom: -80px
      transform: translateX(-50%)
      background: linear-gradient(221.29deg, #73D14F 18.47%, #9B51E0 83.58%)
      border-radius: 50%
      z-index: -1

      +respond-to('small')
        width: 1085px
        height: 1085px
        bottom: -100px

      +respond-to('medium')
        width: 2200px
        height: 2200px
        top: 50%
        right: 0
        bottom auto
        left: -90px
        transform: translateY(-50%)
        background: linear-gradient(221.29deg, #73D14F 18.47%, #9B51E0 83.58%)

  .header
    margin-bottom: 20px

    +respond-to('small')
      margin-bottom: 50px

  .form
    margin-bottom: 20px

  .license
    margin-top: 20px
    font-weight: 400
    font-size: 12px
    line-height: 15px
    color: #fff

    a
      text-decoration: underline

  .login
    display: flex

    p, a
      +base-text
      color: #fff

    p
      margin: 0 5px 0 0

    a
      text-decoration: underline
</style>
